import React from "react";

import { useNavigate } from "react-router-dom";

import { useGetBillingDashboardQuery } from "../../slices/billingDashboardApiSlice";

const BillingDashboardScreen = () => {
  const navigate = useNavigate();

  const { data, isLoading, error } = useGetBillingDashboardQuery();

  const dashboard = data?.data;

  // =====================================================
  // Helpers
  // =====================================================

  const formatCurrency = (amount = 0) => {
    return `₹${Number(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // =====================================================
  // Loading
  // =====================================================

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div
          className="
            animate-spin
            h-10
            w-10
            border-4
            border-blue-600
            border-t-transparent
            rounded-full
          "
        />
      </div>
    );
  }

  // =====================================================
  // Error
  // =====================================================

  if (error) {
    return (
      <div
        className="
          bg-red-50
          border
          border-red-200
          text-red-700
          rounded-lg
          p-5
        "
      >
        <p className="font-semibold">Unable to load billing dashboard</p>

        <p className="text-sm mt-1">
          {error?.data?.message ||
            error?.error ||
            "Something went wrong while loading billing data."}
        </p>
      </div>
    );
  }

  // =====================================================
  // Safe Dashboard Values
  // =====================================================

  const centers = dashboard?.centers || {};
  const users = dashboard?.users || {};
  const usage = dashboard?.usage || {};
  const revenue = dashboard?.revenue || {};
  const invoices = dashboard?.invoices || {};
  const recentInvoices = dashboard?.recentInvoices || [];

  // =====================================================
  // Render
  // =====================================================

  return (
    <div className="space-y-6">
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div>
        <h1 className="text-3xl font-bold text-gray-800">Billing Dashboard</h1>

        <p className="text-gray-500 mt-1">
          Coaching center billing and revenue overview
        </p>
      </div>

      {/* =====================================================
          COACHING CENTER STATISTICS
      ===================================================== */}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
        <DashboardCard
          title="Total Centers"
          value={centers.total}
          color="bg-blue-600"
        />

        <DashboardCard
          title="Active Centers"
          value={centers.active}
          color="bg-green-600"
        />

        <DashboardCard
          title="Trial Centers"
          value={centers.trial}
          color="bg-yellow-500"
          text="text-gray-900"
        />

        <DashboardCard
          title="Paid Centers"
          value={centers.paid}
          color="bg-cyan-600"
        />
      </div>

      {/* =====================================================
          REVENUE OVERVIEW
      ===================================================== */}

      <div>
        <div className="mb-3">
          <h2 className="text-xl font-semibold text-gray-800">
            Revenue Overview
          </h2>

          <p className="text-sm text-gray-500">
            Billing amounts across coaching centers
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-5">
          <RevenueCard
            title="Total Revenue"
            amount={revenue.total}
            color="text-green-600"
            subtitle="Including GST"
          />

          <RevenueCard
            title="Taxable Revenue"
            amount={revenue.taxable}
            color="text-blue-600"
            subtitle="Before GST"
          />

          <RevenueCard
            title="GST Collected"
            amount={revenue.taxCollected}
            color="text-purple-600"
            subtitle="Total GST"
          />

          <RevenueCard
            title="Current Month"
            amount={revenue.currentMonth}
            color="text-cyan-600"
            subtitle="Including GST"
          />

          <RevenueCard
            title="MRR"
            amount={revenue.mrr}
            color="text-yellow-600"
            subtitle="Current billing period"
          />
        </div>
      </div>

      {/* =====================================================
          REVENUE BREAKDOWN
      ===================================================== */}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <SimpleRevenueCard
          title="Current Month Taxable"
          amount={revenue.currentMonthTaxable}
          subtitle="Before GST"
        />

        <SimpleRevenueCard
          title="Current Month GST"
          amount={revenue.currentMonthTaxCollected}
          subtitle="GST collected"
        />

        <SimpleRevenueCard
          title="Taxable MRR"
          amount={revenue.taxableMrr}
          subtitle="MRR before GST"
        />
      </div>

      {/* =====================================================
          MRR TAX INFORMATION
      ===================================================== */}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="font-semibold text-gray-800">
              Current Recurring Billing
            </h3>

            <p className="text-sm text-gray-500 mt-1">
              Active billing-period invoices from coaching centers
            </p>
          </div>

          <div className="text-right">
            <p className="text-sm text-gray-500">GST component of MRR</p>

            <p className="text-xl font-bold text-purple-600 mt-1">
              {formatCurrency(revenue.monthlyRecurringTax)}
            </p>
          </div>
        </div>
      </div>

      {/* =====================================================
          INVOICE SUMMARY
      ===================================================== */}

      <div>
        <div className="mb-3">
          <h2 className="text-xl font-semibold text-gray-800">
            Invoice Summary
          </h2>

          <p className="text-sm text-gray-500">
            Invoice totals by billing status
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          <SummaryCard
            title="Paid"
            count={invoices.paid?.count}
            amount={invoices.paid?.amount}
            taxableAmount={invoices.paid?.taxableAmount}
            taxAmount={invoices.paid?.taxAmount}
            color="text-green-600"
          />

          <SummaryCard
            title="Pending"
            count={invoices.pending?.count}
            amount={invoices.pending?.amount}
            taxableAmount={invoices.pending?.taxableAmount}
            taxAmount={invoices.pending?.taxAmount}
            color="text-yellow-600"
          />

          <SummaryCard
            title="Cancelled"
            count={invoices.cancelled?.count}
            amount={invoices.cancelled?.amount}
            taxableAmount={invoices.cancelled?.taxableAmount}
            taxAmount={invoices.cancelled?.taxAmount}
            color="text-red-600"
          />

          <SummaryCard
            title="Overdue"
            count={invoices.overdue?.count}
            amount={invoices.overdue?.amount}
            taxableAmount={invoices.overdue?.taxableAmount}
            taxAmount={invoices.overdue?.taxAmount}
            color="text-orange-600"
          />
        </div>
      </div>

      {/* =====================================================
          USERS & USAGE
      ===================================================== */}

      <div>
        <div className="mb-3">
          <h2 className="text-xl font-semibold text-gray-800">
            Platform Usage
          </h2>

          <p className="text-sm text-gray-500">
            Current coaching center user and quiz activity
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          <SimpleCard title="Center Admins" value={users.centerAdmins} />

          <SimpleCard title="Students" value={users.students} />

          <SimpleCard
            title="Total Quiz Attempts"
            value={usage.totalQuizAttempts}
          />

          <SimpleCard
            title="Current Month Attempts"
            value={usage.currentMonthAttempts}
          />
        </div>
      </div>

      {/* =====================================================
          RECENT INVOICES
      ===================================================== */}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
            <div>
              <h2 className="font-semibold text-lg text-gray-800">
                Recent Invoices
              </h2>

              <p className="text-sm text-gray-500 mt-1">
                Latest billing activity across coaching centers
              </p>
            </div>

            <button
              onClick={() => navigate("/billing/invoices")}
              className="
                px-4
                py-2
                border
                border-blue-500
                text-blue-600
                rounded-lg
                hover:bg-blue-50
                text-sm
                font-medium
              "
            >
              View All Invoices
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">
                  Invoice
                </th>

                <th className="px-4 py-3 text-left font-semibold text-gray-700">
                  Coaching Center
                </th>

                <th className="px-4 py-3 text-left font-semibold text-gray-700">
                  Invoice Date
                </th>

                <th className="px-4 py-3 text-right font-semibold text-gray-700">
                  Taxable
                </th>

                <th className="px-4 py-3 text-right font-semibold text-gray-700">
                  Discount
                </th>

                <th className="px-4 py-3 text-right font-semibold text-gray-700">
                  GST
                </th>

                <th className="px-4 py-3 text-right font-semibold text-gray-700">
                  Total
                </th>

                <th className="px-4 py-3 text-left font-semibold text-gray-700">
                  Status
                </th>

                <th className="px-4 py-3 text-right font-semibold text-gray-700">
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {recentInvoices.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-10 text-gray-500">
                    No invoices found
                  </td>
                </tr>
              ) : (
                recentInvoices.map((invoice) => (
                  <tr
                    key={invoice._id}
                    className="
                      border-b
                      last:border-b-0
                      hover:bg-gray-50
                    "
                  >
                    {/* Invoice */}

                    <td className="px-4 py-4">
                      <button
                        onClick={() =>
                          navigate(`/billing/invoices/${invoice._id}`)
                        }
                        className="
                          font-semibold
                          text-blue-600
                          hover:text-blue-800
                          hover:underline
                        "
                      >
                        {invoice.invoiceNumber || "-"}
                      </button>

                      <div className="text-xs text-gray-500 mt-1">
                        {invoice.gstType
                          ? invoice.gstType.toUpperCase()
                          : "GST"}
                      </div>
                    </td>

                    {/* Coaching Center */}

                    <td className="px-4 py-4">
                      <div className="font-medium text-gray-800">
                        {invoice.coachingCenter?.name ||
                          invoice.coachingCenterName ||
                          "-"}
                      </div>

                      {invoice.coachingCenter?.email && (
                        <div className="text-xs text-gray-500 mt-1">
                          {invoice.coachingCenter.email}
                        </div>
                      )}
                    </td>

                    {/* Date */}

                    <td className="px-4 py-4 whitespace-nowrap">
                      {formatDate(invoice.invoiceDate)}
                    </td>

                    {/* Taxable */}

                    <td className="px-4 py-4 text-right whitespace-nowrap">
                      {formatCurrency(invoice.taxableAmount)}
                    </td>

                    {/* Discount */}

                    <td className="px-4 py-4 text-right whitespace-nowrap">
                      {Number(invoice.discountAmount || 0) > 0 ? (
                        <span className="text-red-600">
                          -{formatCurrency(invoice.discountAmount)}
                        </span>
                      ) : (
                        formatCurrency(0)
                      )}
                    </td>

                    {/* GST */}

                    <td className="px-4 py-4 text-right whitespace-nowrap">
                      <div className="font-medium">
                        {formatCurrency(invoice.totalTaxAmount)}
                      </div>

                      <div className="text-xs text-gray-500 mt-1">
                        {invoice.gstRate || 0}%
                      </div>
                    </td>

                    {/* Total */}

                    <td className="px-4 py-4 text-right whitespace-nowrap">
                      <div className="font-bold text-gray-900">
                        {formatCurrency(invoice.total)}
                      </div>

                      {Number(invoice.paidAmount || 0) > 0 && (
                        <div className="text-xs text-green-600 mt-1">
                          Paid {formatCurrency(invoice.paidAmount)}
                        </div>
                      )}
                    </td>

                    {/* Status */}

                    <td className="px-4 py-4">
                      <InvoiceStatus status={invoice.status} />
                    </td>

                    {/* Action */}

                    <td className="px-4 py-4 text-right">
                      <button
                        onClick={() =>
                          navigate(`/billing/invoices/${invoice._id}`)
                        }
                        className="
                          px-3
                          py-1.5
                          border
                          border-blue-500
                          text-blue-600
                          rounded-lg
                          hover:bg-blue-50
                          text-xs
                          font-medium
                        "
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// =====================================================
// Dashboard Card
// =====================================================

const DashboardCard = ({ title, value, color, text = "text-white" }) => {
  return (
    <div
      className={`
        ${color}
        ${text}
        rounded-xl
        shadow-sm
        p-5
      `}
    >
      <p className="text-sm opacity-90">{title}</p>

      <h2 className="text-3xl font-bold mt-2">{value ?? 0}</h2>
    </div>
  );
};

// =====================================================
// Revenue Card
// =====================================================

const RevenueCard = ({ title, amount, color, subtitle }) => {
  return (
    <div
      className="
        bg-white
        rounded-xl
        border
        border-gray-200
        shadow-sm
        p-5
      "
    >
      <p className="text-sm text-gray-500">{title}</p>

      <h3
        className={`
          text-2xl
          font-bold
          mt-2
          ${color}
        `}
      >
        ₹
        {Number(amount || 0).toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </h3>

      {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
    </div>
  );
};

// =====================================================
// Simple Revenue Card
// =====================================================

const SimpleRevenueCard = ({ title, amount, subtitle }) => {
  return (
    <div
      className="
        bg-white
        rounded-xl
        border
        border-gray-200
        shadow-sm
        p-5
      "
    >
      <p className="text-sm text-gray-500">{title}</p>

      <h3 className="text-xl font-bold mt-2 text-gray-800">
        ₹
        {Number(amount || 0).toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </h3>

      {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
    </div>
  );
};

// =====================================================
// Invoice Summary Card
// =====================================================

const SummaryCard = ({
  title,
  count,
  amount,
  taxableAmount,
  taxAmount,
  color,
}) => {
  return (
    <div
      className="
        bg-white
        rounded-xl
        border
        border-gray-200
        shadow-sm
        p-5
      "
    >
      <p className="text-sm text-gray-500">{title} Invoices</p>

      <h2
        className={`
          text-3xl
          font-bold
          mt-2
          ${color}
        `}
      >
        {count ?? 0}
      </h2>

      <p className="mt-2 font-semibold text-gray-800">
        {formatCardCurrency(amount)}
      </p>

      <div className="mt-3 text-xs text-gray-500 space-y-1">
        <p>
          Taxable:{" "}
          <span className="font-medium text-gray-700">
            {formatCardCurrency(taxableAmount)}
          </span>
        </p>

        <p>
          GST:{" "}
          <span className="font-medium text-gray-700">
            {formatCardCurrency(taxAmount)}
          </span>
        </p>
      </div>
    </div>
  );
};

// =====================================================
// Simple Card
// =====================================================

const SimpleCard = ({ title, value }) => {
  return (
    <div
      className="
        bg-white
        rounded-xl
        border
        border-gray-200
        shadow-sm
        p-5
      "
    >
      <p className="text-sm text-gray-500">{title}</p>

      <h2 className="text-3xl font-bold mt-2 text-gray-800">{value ?? 0}</h2>
    </div>
  );
};

// =====================================================
// Invoice Status
// =====================================================

const InvoiceStatus = ({ status }) => {
  const styles = {
    paid: "bg-green-100 text-green-700",
    pending: "bg-yellow-100 text-yellow-700",
    cancelled: "bg-red-100 text-red-700",
    overdue: "bg-orange-100 text-orange-700",
  };

  const labels = {
    paid: "Paid",
    pending: "Pending",
    cancelled: "Cancelled",
    overdue: "Overdue",
  };

  return (
    <span
      className={`
        inline-flex
        px-3
        py-1
        rounded-full
        text-xs
        font-semibold
        whitespace-nowrap
        ${styles[status] || "bg-gray-100 text-gray-700"}
      `}
    >
      {labels[status] || status || "Unknown"}
    </span>
  );
};

// =====================================================
// Currency Helper For Summary Cards
// =====================================================

const formatCardCurrency = (amount = 0) => {
  return `₹${Number(amount || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export default BillingDashboardScreen;
